import React, {useState} from 'react';
import {Button, Paper, Stack, SxProps} from "@mui/material"
import {RowOrigin} from "../../api/enum/RowOrigin"
import RowJson from "../../api/json/RowJson"
import {useProjectData} from "../../hooks/useProjectData"
import EditIcon from "@mui/icons-material/Edit"
import AddIcon from "@mui/icons-material/Add"
import TemplateUtil from "../../library/TemplateUtil"
import TemplateRendererSection from "../templates/TemplateRendererSection"
import CreateRowModal from "../rows/CreateRowModal"
import {CompletionResultJson} from "../../api/json/CompletionResultJson"
import {GenerativeResultJson} from "../../api/json/GenerativeResultJson"
import {AnyTemplateJson} from "../../api/json/TemplateJson"
import {DisplayFormat} from "../../api/enum/DisplayFormat"

interface CompletionCardProps {
    generativeResult: GenerativeResultJson
    template: AnyTemplateJson
    referenceValues: Record<string, string>
    showPrompt?: boolean
    display?: DisplayFormat
    sx?: SxProps
}

function CompletionCard({generativeResult, template, referenceValues, display, sx}: CompletionCardProps) {
    const [rowEditorOpen, setRowEditorOpen] = useState(false)
    const [savedExample, setSavedExample] = useState<RowJson | null>(generativeResult.row)

    const {project, reloadProject} = useProjectData()

    let completionText: string
    if (savedExample) {
        completionText = TemplateUtil.renderCompletionEquivalent(template, savedExample.cells, project?.columns)
    } else if ("text" in generativeResult) {
        completionText = (generativeResult as CompletionResultJson).text
    } else {
        completionText = TemplateUtil.renderCompletionEquivalent(template, generativeResult.referenceValues, project?.columns)
    }

    return (
        <>
            <Paper variant="outlined" sx={{...sx, p: 2, whiteSpace: "pre-wrap"}}>
                <Stack spacing={2} alignItems="flex-start">
                    <TemplateRendererSection
                        value={completionText}
                        display={display ?? DisplayFormat.PLAIN_TEXT}
                        dynamicSize={false}
                    />
                    {savedExample ?
                        <Button variant="text"
                                size="small"
                                startIcon={<EditIcon/>}
                                onClick={() => setRowEditorOpen(true)}
                                sx={{minWidth: 90}}
                        >
                            Edit
                        </Button>
                        : <Button variant="outlined"
                                  size="small"
                                  startIcon={<AddIcon/>}
                                  onClick={() => setRowEditorOpen(true)}
                                  sx={{minWidth: 90}}
                        >
                            Add
                        </Button>
                    }
                </Stack>
            </Paper>

            <CreateRowModal
                key={savedExample?.id}
                open={rowEditorOpen}
                onClose={() => setRowEditorOpen(false)}
                project={project!}
                row={savedExample ?? undefined}
                origin={RowOrigin.PLAYGROUND}
                generativeResultId={generativeResult.id}
                referenceValues={referenceValues}
                didCreate={(_, row) => {
                    setSavedExample(row)
                    setRowEditorOpen(false)
                    reloadProject() // To update the row count
                }}
                didUpdate={(row) => {
                    setSavedExample(row)
                    setRowEditorOpen(false)
                }}
                didDelete={() => {
                    setSavedExample(null)
                    setRowEditorOpen(false)
                }}
            />
        </>
    );
}

export default CompletionCard;
